import { Icon, Modal, TextArea } from '@successmode-ktp/kui';
import ICON_SEND from '@/assets/sideBar/send.png';
import styled, { css } from 'styled-components';
import { useEffect, useMemo, useState } from 'react';
import { comma } from '@/util/format';
import { useMutation, useQueryClient } from 'react-query';
import { sendChat } from '@/api/cs';
import { IExcelData } from '@/types/refund';
import { refundStatusDivision } from '@/util/common';
import ICON_CS from '@/assets/sideBar/CS.png';
import ICON_KAKAO from '@/assets/sideBar/Kakao.png';
import useGetGlobalStatus from '@/hooks/useGetGlobalStatus';
import { RequestEditRefundChat } from '@/types/cs.type';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  data: IExcelData[];
  refundHistoryStatus: string;
  chatId: number;
};

function RequestEditModal({
  isOpen,
  onClose,
  data,
  refundHistoryStatus,
  chatId,
}: Props) {
  const { globalStatus } = useGetGlobalStatus();
  const queryClient = useQueryClient();

  const [requestReason, setRequestReason] = useState('');
  const [isSuccessRequest, setIsSuccessRequest] = useState(false);

  const { mutate: mutateSendChat } = useMutation(sendChat);
  const onRequestEditRefund = () => {
    const info = {
      refundId: data[0].refundIndex,
      supplyDate: data[0].saleDate,
      touristName: data[0].patientName,
      passportNumber: data[0].patientPassport,
      nationality: data[0].patientNation,
      productAmount: Number(data[0].totalPayment),
      refundAmount: Number(data[0].totalRefund),
      requestReason,
    };
    const payload = !!chatId
      ? { ...info, messageType: 'APPEND_REFUND_MODIFY_ACCEPTED', chatId }
      : { ...info, messageType: 'REFUND_MODIFY_ACCEPTED' };
    mutateSendChat(payload as RequestEditRefundChat, {
      onSuccess: () => {
        queryClient.invalidateQueries('refundDetail');
        setIsSuccessRequest(true);
      },
    });
  };

  const [refundInfo, setRefundInfo] = useState<Record<string, any>>({
    refundHistoryStatus,
    saleDate: data[0].saleDate,
    patientNation: data[0].patientNation,
    patientName: data[0].patientName,
    credit: 0,
    cash: 0,
    totalRefund: 0,
    serviceTotalPayment: 0,
    totalPayment: 0,
  });

  useEffect(() => {
    let credit = 0;
    let cash = 0;
    let totalRefund = 0;
    let serviceTotalPayment = 0;
    let totalPayment = 0;

    data.forEach((item) => {
      credit += Number(item.credit);
      cash += Number(item.cash);
      totalRefund += Number(item.totalRefund);
      serviceTotalPayment += Number(item.serviceTotalPayment);
      totalPayment += Number(item.totalPayment);
    });

    setRefundInfo({
      refundHistoryStatus,
      saleDate: data[0].saleDate,
      patientNation: data[0].patientNation,
      patientName: data[0].patientName,
      credit,
      cash,
      totalRefund,
      serviceTotalPayment,
      totalPayment,
    });
  }, [data]);

  const isGroupRefund = useMemo(() => data.length > 1, [data]);

  const TABLE_HEADER =
    globalStatus?.paymentType === 'ORIGINAL'
      ? [
          '환급 상태',
          '시술 일자',
          '국적',
          '고객 성함',
          '의료 금액',
          '환급액',
          '최종 결제 금액',
        ]
      : [
          '환급 상태',
          '시술 일자',
          '국적',
          '고객 성함',
          '의료 금액',
          '카드 결제액',
          '현금 결제액',
          '환급예정금액',
          '최종 결제 금액',
        ];

  return (
    <>
      <Modal isOpen={isOpen} hasBackdrop>
        <Modal.Header>환급 수정 요청</Modal.Header>
        <Body>
          <Wrapper>
            <Desc>수정할 환급건이 맞는지 확인해주세요</Desc>
            <Table>
              <Thead>
                <Tr>
                  {TABLE_HEADER.map((tr) => (
                    <Th key={tr}>{tr}</Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td style={{ color: '#CBCCCE' }}>
                    {refundStatusDivision(refundInfo.refundHistoryStatus)}
                  </Td>
                  <Td>{refundInfo.saleDate}</Td>
                  <Td style={{ color: '#CBCCCE' }}>
                    {refundInfo.patientNation}
                  </Td>
                  <Td>{refundInfo.patientName}</Td>
                  <Td>{comma(refundInfo.serviceTotalPayment)}원</Td>
                  {globalStatus?.paymentType === 'DIRECT' && (
                    <Td>{comma(refundInfo.credit)}원</Td>
                  )}
                  {globalStatus?.paymentType === 'DIRECT' && (
                    <Td>{comma(refundInfo.cash)}원</Td>
                  )}
                  <Td style={{ color: '#CBCCCE' }}>
                    {comma(refundInfo.totalRefund)}원
                  </Td>
                  <Td>{comma(refundInfo.totalPayment)}원</Td>
                </Tr>
              </Tbody>
            </Table>
          </Wrapper>
          {!isGroupRefund && (
            <Wrapper>
              <Desc>
                변경할 내용
                <Red>
                  * '환급상태', '국적', '환급액' 정보는 변경 불가능합니다
                </Red>
              </Desc>
              <TextArea
                value={requestReason}
                onChange={(e) => setRequestReason(e.target.value)}
                placeholder='수정할 내용을 작성해주세요 (예 : 고객 성함  SOFIA KRISTINA > SARA KRISTINA 로 변경)'
                size='sm'
                rows={4}
              />
            </Wrapper>
          )}
          {isGroupRefund && (
            <ContactWrapper>
              <ContactDesc>
                <ContactDescTitle>
                  해당건은 전화 및 카카오톡 문의하기로 수정이 가능합니다. 아래
                  채널을 이용해주세요
                </ContactDescTitle>
                <Red>
                  * '시술 일자', '시술 금액', '고객 성함' 정보만 변경 가능합니다
                </Red>
              </ContactDesc>
              <ContactList>
                <ContactItem>
                  <ContactIcon style={{ backgroundColor: '#7CA7FA' }}>
                    <img src={ICON_CS} alt='' width={18} />
                  </ContactIcon>
                  <Contact>02-6275-8011</Contact>
                </ContactItem>
                <ContactItem>
                  <ContactIcon style={{ backgroundColor: '#FAE100' }}>
                    <img src={ICON_KAKAO} alt='' width={24} />
                  </ContactIcon>
                  <Contact>
                    <KakaoLink
                      href='http://pf.kakao.com/_fFgsb/chat'
                      target='_blank'
                    >
                      카카오톡 채널로 수정 요청하기
                    </KakaoLink>
                  </Contact>
                </ContactItem>
              </ContactList>
            </ContactWrapper>
          )}
        </Body>
        <Modal.Footer>
          <Modal.Button variant='cancel' onClick={onClose} />
          {!isGroupRefund && (
            <Modal.Button
              variant='ok'
              onClick={onRequestEditRefund}
              disabled={!requestReason.trim()}
            >
              <img
                src={ICON_SEND}
                alt=''
                width={20}
                style={{ marginRight: '6px' }}
              />
              수정 요청 보내기
            </Modal.Button>
          )}
        </Modal.Footer>
      </Modal>

      <Modal isOpen={isSuccessRequest} hasBackdrop order={2}>
        <SuccessModalWrapper>
          <Icon.CheckFilled size={64} fillColor='#246CF6' />
          <SuccessTitle>환급 수정이 요청되었습니다</SuccessTitle>
        </SuccessModalWrapper>
        <Modal.Footer>
          <Modal.Button
            variant='ok'
            onClick={() => {
              setRequestReason('');
              setIsSuccessRequest(false);
              onClose();
            }}
          />
        </Modal.Footer>
      </Modal>
    </>
  );
}

const Body = styled.div`
  min-width: 720px;
  width: 100%;
  max-width: 95vw;
  margin-top: 16px;
`;

const Wrapper = styled.div`
  &:first-child {
    margin-bottom: 16px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-radius: 12px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 0 0 1px #e5e6e8;
`;

const Thead = styled.thead`
  background: #f5f6f7;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr<{ index?: number }>`
  ${({ index }) =>
    index &&
    index > 0 &&
    css`
      border-top: 1px solid #e5e6e8;
    `}
`;

const Th = styled.th`
  color: #80848a;
  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
  height: 38px;
  vertical-align: middle;
  padding: 0 12px;
  white-space: nowrap;
`;

const Td = styled.td`
  text-align: center;
  color: #5f6165;
  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
  height: 49px;
  vertical-align: middle;
  padding: 0 12px;
  white-space: nowrap;
`;

const Desc = styled.p`
  margin-bottom: 16px;
  color: #5f6165;
  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
`;

const Red = styled.span`
  margin-left: 4px;
  color: #ff2d55;
`;

const SuccessModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  width: 436px;
`;

const SuccessTitle = styled.strong`
  color: #030303;
  text-align: center;
  font-size: 21px;
  font-weight: 700;
`;

const ContactWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 24px;
  width: 100%;
  margin-top: 20px;
`;

const ContactDesc = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
`;

const ContactDescTitle = styled.p`
  color: #5f6165;
  font-size: 14px;
  font-weight: 600;
  line-height: 21px;
  text-align: center;
`;

const ContactList = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 48px;
  width: 100%;
`;

const ContactItem = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:nth-child(1) {
    &:after {
      content: '';
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      right: -24px;
      background-color: #cbccce;
      width: 1px;
      height: 32px;
    }
  }
`;

const ContactIcon = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 100px;
`;

const Contact = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 212px;
  background-color: #f5f6f7;
  border-radius: 100px;
  height: 36px;
  color: #5f6165;
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
`;
const KakaoLink = styled.a`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default RequestEditModal;
